import { useTranslation } from 'react-i18next';
import { Input, Button } from '@parrot-software/design-system';
import { Search, Plus } from '@parrot-software/design-system/icons';
import { useHistory } from 'react-router-dom';

interface RolesSearchBarProps {
  /** Current search query value (controlled component) */
  value?: string;
  /** Callback when search query changes */
  onSearch: (query: string) => void;
  /** Whether the search input is disabled (e.g., during loading) */
  disabled?: boolean;
}

/**
 * Roles Search Bar Component
 *
 * Search input and create button for roles list.
 * Uses Semantic Styles Pattern for maintainability.
 *
 * @param {RolesSearchBarProps} props - Component properties
 * @returns {React.ReactElement} Roles search bar component
 */
const styles = {
  container: 'flex items-center justify-between w-full',
  searchWrapper: 'w-[280px]',
  searchContainer: 'relative',
  searchInput: 'pr-10',
  searchIcon: 'absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground',
  buttonIcon: 'size-4',
};

export const RolesSearchBar = ({ value = '', onSearch, disabled = false }: RolesSearchBarProps): React.ReactElement => {
  const { t } = useTranslation('roles');
  const history = useHistory();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onSearch(newValue);
  };

  const handleCreateClick = () => {
    history.push('/roles/create');
  };

  return (
    <div className={styles.container}>
      {/* Search Input */}
      <div className={styles.searchWrapper}>
        <div className={styles.searchContainer}>
          <Input
            className={styles.searchInput}
            disabled={disabled}
            onChange={handleSearchChange}
            placeholder={t('list.searchPlaceholder')}
            type="text"
            value={value}
          />
          <Search className={styles.searchIcon} />
        </div>
      </div>

      {/* Create Button */}
      <Button onClick={handleCreateClick} type="button" variant="default">
        <Plus className={styles.buttonIcon} />
        {t('list.createButton')}
      </Button>
    </div>
  );
};
